"use client";

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { OverlayActions, OverlayEntry } from "./overlay-registry.types";

export type { OverlayEntry } from "./overlay-registry.types";

// Actions context: stable reference, never changes → safe for useEffect deps
const OverlayActionsContext = createContext<OverlayActions | null>(null);

// State context: changes when overlays list changes → only debug panel subscribes
const OverlayStateContext = createContext<OverlayEntry[]>([]);

function safeMetadata(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (/[/\\]\.\.|[?#]|password|secret|token|cookie/i.test(value)) {
    return "[redacted]";
  }
  return value
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .trim()
    .slice(0, 160);
}

export function OverlayRegistryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [overlays, setOverlays] = useState<OverlayEntry[]>([]);

  const actions = useMemo<OverlayActions>(
    () => ({
      register: (entry) => {
        const safeEntry = {
          ...entry,
          sourcePath: safeMetadata(entry.sourcePath) ?? "[redacted]",
          label: safeMetadata(entry.label),
        };
        setOverlays((prev) =>
          prev.some((o) => o.id === safeEntry.id) ? prev : [...prev, safeEntry],
        );
      },
      unregister: (id) => {
        setOverlays((prev) => {
          const next = prev.filter((o) => o.id !== id);
          return next.length === prev.length ? prev : next;
        });
      },
    }),
    [],
  );

  return (
    <OverlayActionsContext.Provider value={actions}>
      <OverlayStateContext.Provider value={overlays}>
        {children}
      </OverlayStateContext.Provider>
    </OverlayActionsContext.Provider>
  );
}

/**
 * Returns register/unregister actions. Stable reference — safe for useEffect deps.
 * Used by Dialog/Sheet/Drawer base components.
 */
export function useOverlayActions() {
  return useContext(OverlayActionsContext);
}

/**
 * Returns the current overlay entries list. Triggers re-render on change.
 * Used by the debug panel to display open overlays.
 */
export function useOverlayState() {
  return useContext(OverlayStateContext);
}

/**
 * @deprecated Use useOverlayActions() + useOverlayState() separately.
 */
export function useOverlayRegistry() {
  const actions = useContext(OverlayActionsContext);
  const overlays = useContext(OverlayStateContext);
  if (!actions) return null;
  return { ...actions, overlays };
}

/**
 * Hook to auto-register/unregister an overlay in the debug registry.
 * Call this in any Dialog/Sheet/Drawer component.
 */
export function useDebugOverlay(
  type: OverlayEntry["type"],
  sourcePath: string | undefined,
  isOpen: boolean,
  label?: string,
) {
  const actions = useContext(OverlayActionsContext);
  const reactId = useId();
  const idRef = useRef(`${type}-${sourcePath}-${reactId}`);

  useEffect(() => {
    if (!actions || !sourcePath) return;
    const overlayId = idRef.current;

    if (isOpen) {
      actions.register({ id: overlayId, type, sourcePath, label });
    } else {
      actions.unregister(overlayId);
    }

    return () => {
      actions.unregister(overlayId);
    };
  }, [isOpen, actions, type, sourcePath, label]);
}
